/**
 * Qué pide la lista al backend y qué cuenta el encabezado (bug «Por responder», 2026-10-05).
 *
 * «Por responder» pedía la bandeja entera (`folder=inbox`, sin categoría) y la filtraba en el
 * navegador, pero el encabezado mostraba el `total`/`unread` de esa bandeja entera: «28
 * conversaciones · 10 sin leer» sobre una lista vacía. El backend ya filtra por
 * `category=needs_reply` (con su total), así que la lista y el contador salen ahora de ahí.
 */

/** Carpeta y categoría que la lista pide a `/mail/unified`. */
export function peticionDeCarpeta(carpeta, pestana, busquedaEnServidor) {
  if (carpeta === 'snoozed' || carpeta === 'scheduled') return { carpeta: null, categoria: null };
  if (carpeta === 'needs_reply') {
    return { carpeta: 'inbox', categoria: busquedaEnServidor ? null : 'needs_reply' };
  }
  return { carpeta, categoria: carpeta === 'inbox' && !busquedaEnServidor ? pestana : null };
}

/**
 * Texto del encabezado. El total y los no leídos del servidor solo valen si la lista que se ve
 * es esa misma respuesta (sin filtro de cuenta ni de texto en el navegador); si no, se cuenta lo
 * que se ve. Una carpeta vacía nunca dice «sin leer».
 */
export function contadorDeCarpeta({ primeraPagina, visibles, delServidor, pestana }) {
  const delSrv = (k) => (delServidor && typeof primeraPagina?.[k] === 'number' ? primeraPagina[k] : null);
  const total = delSrv('total') ?? visibles.length;
  const sinLeer = total === 0 ? 0 : (delSrv('unread') ?? visibles.filter((m) => !m.seen).length);
  // Bug 3: pestaña vacía con no leídos en otras → decirlo, si no el usuario no sabe dónde están
  const otras = total === 0 && pestana && delServidor ? sinLeerEnOtrasPestanas(primeraPagina, pestana.id) : 0;
  const texto = otras > 0
    ? `0 en ${pestana.etiqueta} · ${otras} sin leer en otras pestañas`
    : [
      `${total} ${total === 1 ? 'conversación' : 'conversaciones'}`,
      sinLeer > 0 ? `${sinLeer} sin leer` : null,
    ].filter(Boolean).join(' · ');
  return { total, sinLeer, texto };
}

/* Pestañas que reparten Recibidos (cada correo en una sola; server.py PESTANAS_BANDEJA). «Por
   responder» se cruza con ellas: no suma ni resta. Los conteos los da el backend (`pestanas`). */
const PESTANAS_QUE_REPARTEN = ['primary', 'updates', 'promotions', 'social'];

/** No leídos de Recibidos fuera de la pestaña `id`, con los conteos del backend. */
export function sinLeerEnOtrasPestanas(respuesta, id) {
  if (!respuesta?.pestanas || typeof respuesta.sin_leer_pestanas !== 'number') return 0;
  const propia = PESTANAS_QUE_REPARTEN.includes(id) ? (respuesta.pestanas[id]?.unread || 0) : 0;
  return Math.max(0, respuesta.sin_leer_pestanas - propia);
}

/** Badge de Recibidos en la barra lateral: la suma de sus pestañas (si el backend la da). */
export function badgeRecibidos(respuesta) {
  if (typeof respuesta?.sin_leer_pestanas === 'number') return respuesta.sin_leer_pestanas;
  return respuesta?.unread ?? 0;
}

/**
 * Qué decir con una pestaña de Recibidos vacía (QA 2026-10-06): antes salía «Bandeja vacía · No hay correos en
 * Recibidos» aunque hubiera correo en otras pestañas. `conteo` = `pestanas` de /mail/unified ({id: {total, unread}}),
 * o null si no viene (p. ej. filtrando una sola cuenta).
 */
export function vacioDePestana(tab, etiqueta, conteo) {
  const otras = conteo
    ? PESTANAS_QUE_REPARTEN.filter((id) => id !== tab).reduce((n, id) => n + (conteo[id]?.total || 0), 0)
    : 0;
  return {
    titulo: `Nada en ${etiqueta}`,
    texto: otras
      ? `No hay correos en ${etiqueta}. Tienes ${otras} en otras pestañas de Recibidos.`
      : `No hay correos en ${etiqueta}. Cuando lleguen mensajes nuevos aparecerán aquí.`,
  };
}
